import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { Activity, Plus, AlertCircle, Stethoscope } from 'lucide-react';
import MedicalConditionCard from './MedicalConditionCard';
import MedicalConditionModal from './MedicalConditionModal';
import {
  ClientMedicalInfoDto,
  medicalConditionsApi,
  handleApiError
} from '../../services/medicalConditionsService';

interface MedicalConditionsSectionProps {
  clientId: number;
  formatDate: (dateString?: string) => string;
}

const Section = styled.div`
  /* No margin - handled by parent */
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--border-color);
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SectionIcon = styled.span`
  color: #d97706;
  display: flex;
  align-items: center;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
`;

const CountBadge = styled.span`
  background: rgba(217, 119, 6, 0.12);
  color: #d97706;
  font-size: 14px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 10px;
  border: 1px solid rgba(217, 119, 6, 0.25);
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubsectionHeader = styled.div<{ $borderColor: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  margin-top: 24px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${props => props.$borderColor};

  &:first-of-type {
    margin-top: 0;
  }
`;

const SubsectionTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
`;

const SubsectionCount = styled.span`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: var(--text-secondary);
`;

const EmptyIconWrapper = styled.div`
  color: var(--text-secondary);
  margin-bottom: 16px;
  opacity: 0.4;
`;

const EmptyText = styled.p`
  margin: 0 0 16px 0;
  font-size: 16px;
  line-height: 1.5;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 48px;
  color: var(--text-secondary);
  font-size: 15px;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
  padding: 14px 18px;
  border-radius: var(--border-radius-md);
  margin-bottom: 18px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid rgba(220, 38, 38, 0.25);
`;

const ConditionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0 -24px;

  @media (max-width: 767px) {
    margin: 0 -16px;
  }
`;

const MedicalConditionsSection: React.FC<MedicalConditionsSectionProps> = ({
  clientId,
  formatDate
}) => {
  const [conditions, setConditions] = useState<ClientMedicalInfoDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState<ClientMedicalInfoDto | null>(null);

  const loadConditions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await medicalConditionsApi.getByClientId(clientId);
      setConditions(data);
    } catch (err) {
      console.error('Error loading medical conditions:', err);
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadConditions();
  }, [loadConditions]);

  const handleAddCondition = () => {
    setSelectedCondition(null);
    setShowModal(true);
  };

  const handleEditCondition = (condition: ClientMedicalInfoDto) => {
    setSelectedCondition(condition);
    setShowModal(true);
  };

  const handleDeleteCondition = async (conditionId: number) => {
    try {
      await medicalConditionsApi.delete(conditionId);
      await loadConditions();
    } catch (err) {
      console.error('Error deleting medical condition:', err);
      toast.error(handleApiError(err));
    }
  };

  const handleModalSuccess = () => {
    loadConditions();
    setShowModal(false);
    setSelectedCondition(null);
  };

  // Separate conditions into primary and secondary
  const primaryConditions = conditions.filter(c => c.isPrimary);
  const secondaryConditions = conditions.filter(c => !c.isPrimary);

  if (loading) {
    return (
      <Section>
        <LoadingContainer>טוען מידע רפואי...</LoadingContainer>
      </Section>
    );
  }

  return (
    <>
      <Section>
        <SectionHeader>
          <TitleGroup>
            <SectionIcon><Activity size={18} strokeWidth={2} /></SectionIcon>
            <SectionTitle>מחלות ואבחונים</SectionTitle>
            {conditions.length > 0 && (
              <CountBadge>{conditions.length}</CountBadge>
            )}
          </TitleGroup>
          <AddButton onClick={handleAddCondition}>
            <Plus size={16} strokeWidth={2} />
            הוספת מחלה
          </AddButton>
        </SectionHeader>

        {error && (
          <ErrorMessage>
            <AlertCircle size={18} strokeWidth={2} />
            {error}
          </ErrorMessage>
        )}

        {conditions.length === 0 ? (
          <EmptyState>
            <EmptyIconWrapper>
              <Stethoscope size={56} strokeWidth={1} />
            </EmptyIconWrapper>
            <EmptyText>
              לא הוגדרו מחלות עבור לקוח זה.<br />
              לחץ על "הוספת מחלה" כדי להוסיף מידע רפואי.
            </EmptyText>
          </EmptyState>
        ) : (
          <>
            {/* Primary Conditions Section */}
            {primaryConditions.length > 0 && (
              <div>
                <SubsectionHeader $borderColor="#dc2626">
                  <SubsectionTitle>מחלות עיקריות</SubsectionTitle>
                  <SubsectionCount>({primaryConditions.length})</SubsectionCount>
                </SubsectionHeader>
                <ConditionsList>
                  {primaryConditions.map(condition => (
                    <MedicalConditionCard
                      key={condition.id}
                      condition={condition}
                      onEdit={() => handleEditCondition(condition)}
                      onDelete={() => handleDeleteCondition(condition.id)}
                      formatDate={formatDate}
                    />
                  ))}
                </ConditionsList>
              </div>
            )}

            {/* Secondary Conditions Section */}
            {secondaryConditions.length > 0 && (
              <div style={{ marginTop: primaryConditions.length > 0 ? '24px' : '0' }}>
                <SubsectionHeader $borderColor="#94a3b8">
                  <SubsectionTitle>מחלות משניות</SubsectionTitle>
                  <SubsectionCount>({secondaryConditions.length})</SubsectionCount>
                </SubsectionHeader>
                <ConditionsList>
                  {secondaryConditions.map(condition => (
                    <MedicalConditionCard
                      key={condition.id}
                      condition={condition}
                      onEdit={() => handleEditCondition(condition)}
                      onDelete={() => handleDeleteCondition(condition.id)}
                      formatDate={formatDate}
                    />
                  ))}
                </ConditionsList>
              </div>
            )}
          </>
        )}
      </Section>

      {/* Modal for Add/Edit */}
      {showModal && (
        <MedicalConditionModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedCondition(null);
          }}
          onSuccess={handleModalSuccess}
          clientId={clientId}
          condition={selectedCondition}
        />
      )}
    </>
  );
};

export default MedicalConditionsSection;
